import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import {
  ParkingRightService,
  ParkerService,
  ParkingTypeService,
  EditParkingRightRequest,
  ParkingRightDto,
  ParkingRightTypeEnum,
  LicensePlateDto,
  TimeBasedParkingRightDto,
  DurationBasedParkingRightDto,
  DayOfWeek,
  CreateParkingRightRequestTypeDto,
  ParkingSpaceDto,
  ParkingZoneDto,
  AssignedZoneParkersDto,
  LongTermParkersDto,
  ListLongTermParkersRequest,
  ParkingTypeEnum
} from '../../../api/gen';
import { JjamesButtonComponent } from 'shared-lib';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';

export interface ParkingRightEditDialogData {
  parkingRight: ParkingRightDto;
  parkingSpace: ParkingSpaceDto;
}

export interface LicensePlateItem {
  id: string;
  fullNumber: string;
  parkerId?: string;
}

@Component({
  selector: 'app-parking-right-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatRadioModule,
    JjamesButtonComponent
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './parking-right-edit.component.html',
  styleUrl: './parking-right-edit.component.scss'
})
export class ParkingRightEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private parkingRightService = inject(ParkingRightService);
  private parkerService = inject(ParkerService);
  private parkingTypeService = inject(ParkingTypeService);
  private dialogRef = inject(MatDialogRef<ParkingRightEditComponent>);
  private dialogData = inject<ParkingRightEditDialogData>(MAT_DIALOG_DATA);
  private snackBar = inject(MatSnackBar);

  form!: FormGroup;
  isLoading = signal(false);
  isLoadingTypes = signal(false);
  isAddingPlate = signal(false);
  licensePlates = signal<LicensePlateItem[]>([]);
  assignedZoneTypes = signal<AssignedZoneParkersDto[]>([]);

  // LongTerm
  longTermTypes = signal<LongTermParkersDto[]>([]);
  isLoadingLongTermTypes = signal(false);

  get isLongTerm(): boolean {
    return this.parkingRight.parkingType?.type?.type === ParkingTypeEnum.LongTerm;
  }

  rightTypeOptions = [
    { value: ParkingRightTypeEnum.TimeBased, label: 'Time Based' },
    { value: ParkingRightTypeEnum.DurationBased, label: 'Duration Based' }
  ];

  daysOfWeek = [
    { value: DayOfWeek.Monday, label: 'Mon' },
    { value: DayOfWeek.Tuesday, label: 'Tue' },
    { value: DayOfWeek.Wednesday, label: 'Wed' },
    { value: DayOfWeek.Thursday, label: 'Thu' },
    { value: DayOfWeek.Friday, label: 'Fri' },
    { value: DayOfWeek.Saturday, label: 'Sat' },
    { value: DayOfWeek.Sunday, label: 'Sun' }
  ];

  get parkingRight(): ParkingRightDto {
    return this.dialogData.parkingRight;
  }

  get parkingSpace(): ParkingSpaceDto {
    return this.dialogData.parkingSpace;
  }

  get availableZones(): ParkingZoneDto[] {
    return this.parkingSpace?.parkingZones || [];
  }

  get selectedRightType(): ParkingRightTypeEnum {
    return this.form?.get('rightType')?.value;
  }

  get isTimeBased(): boolean {
    return this.selectedRightType === ParkingRightTypeEnum.TimeBased;
  }

  get isDurationBased(): boolean {
    return this.selectedRightType === ParkingRightTypeEnum.DurationBased;
  }

  ngOnInit(): void {
    this.initForm();
    this.loadExistingLicensePlates();
    if (this.isLongTerm) {
      this.loadLongTermTypes();
    } else {
      this.loadAssignedZoneTypes();
    }
  }

  private initForm(): void {
    const typeDto = this.parkingRight.parkingRightType as TimeBasedParkingRightDto | DurationBasedParkingRightDto;
    const isTimeBased = typeDto?.type === ParkingRightTypeEnum.TimeBased;
    const timeBasedDto = isTimeBased ? typeDto as TimeBasedParkingRightDto : null;
    const durationBasedDto = !isTimeBased ? typeDto as DurationBasedParkingRightDto : null;

    // Get existing selected zone IDs
    const existingZoneIds = this.parkingRight.allowedParkingZones?.map(z => z.parkingZoneId) || [];

    this.form = this.fb.group({
      parkingTypeId: [this.parkingRight.parkingTypeId || null, Validators.required],
      selectedZones: [existingZoneIds],
      rightType: [this.parkingRight.parkingRightType?.type || ParkingRightTypeEnum.TimeBased, Validators.required],
      licensePlateInput: [''],
      // Time-based fields
      validFrom: [typeDto?.validFrom ? new Date(typeDto.validFrom) : null],
      validTo: [typeDto?.validTo ? new Date(typeDto.validTo) : null],
      startTime: [timeBasedDto?.startTime || ''],
      endTime: [timeBasedDto?.endTime || ''],
      validDays: [timeBasedDto?.validDays || []],
      isAllDay: [timeBasedDto?.isAllDay ?? true],
      isRecurring: [timeBasedDto?.isRecurring ?? false],
      // Duration-based fields
      durationInMinutes: [durationBasedDto?.durationInMinutes || 60],
      isRenewable: [durationBasedDto?.isRenewable ?? false],
      maxRenewals: [durationBasedDto?.maxRenewals || null],
      // LongTerm fields
      name: [this.parkingRight.name || ''],
      validToEnabled: [!!(typeDto?.validTo)],
      // Generic comment (available for all right kinds)
      comment: [this.parkingRight.comment || '']
    });
  }

  private loadExistingLicensePlates(): void {
    // Every plate, not just the first.
    //
    // This used to load `allowedLicensePlates[0]` only, while onSubmit() sends the loaded list as
    // the right's complete set of plates — so opening a two-plate right and saving *any* change
    // silently discarded the second plate. Rights routinely carry several plates now that the
    // hotline can add them, which made that a data-loss bug rather than a cosmetic limit.
    const plates = (this.parkingRight.allowedLicensePlates ?? [])
      .map(lp => ({
        id: lp.licenseId || lp.id || '',
        fullNumber: lp.licensePlateDto?.fullNumber || 'Unknown',
        parkerId: lp.licensePlateDto?.parkersIDs?.at(0)
      }))
      .filter(p => !!p.id);
    this.licensePlates.set(plates);
  }

  private loadAssignedZoneTypes(): void {
    if (!this.parkingSpace?.id || !this.parkingSpace?.createdById) return;

    this.isLoadingTypes.set(true);

    this.parkingTypeService.parkingTypesAssignedZoneParkersFilterPost({
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingSpace.createdById,
      includeDeleted: false
    }).subscribe({
      next: (result) => {
        this.assignedZoneTypes.set(result.items || []);
        this.isLoadingTypes.set(false);
      },
      error: (error) => {
        console.error('Error loading assigned zone types:', error);
        this.snackBar.open('Failed to load parking types', 'Close', { duration: 3000 });
        this.isLoadingTypes.set(false);
      }
    });
  }

  private loadLongTermTypes(): void {
    if (!this.parkingSpace?.id || !this.parkingSpace?.createdById) return;
    this.isLoadingLongTermTypes.set(true);
    const request: ListLongTermParkersRequest = {
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingSpace.createdById,
      includeDeleted: false,
      pageIndex: 0,
      pageSize: 100
    };
    this.parkingTypeService.parkingTypesLongTermParkersFilterPost(request).subscribe({
      next: (result) => {
        this.longTermTypes.set(result.items || []);
        this.isLoadingLongTermTypes.set(false);
      },
      error: () => {
        this.snackBar.open('Failed to load Long-Term types', 'Close', { duration: 3000 });
        this.isLoadingLongTermTypes.set(false);
      }
    });
  }

  addLicensePlate(): void {
    const value = this.form.get('licensePlateInput')?.value?.trim().toUpperCase();
    if (!value) {
      this.snackBar.open('Please enter a license plate number', 'Close', { duration: 3000 });
      return;
    }

    this.isAddingPlate.set(true);

    this.parkerService.parkerRegisterLicensePlatePost({ fullNumber: value }).subscribe({
      next: (result: LicensePlateDto) => {
        if (result.id) {
          const parkerId = result.parkersIDs?.at(0);
          // Append: a right may authorise several vehicles (a company with more than one van).
          // Guarded against duplicates, which would otherwise be sent twice and duplicate the row.
          const alreadyListed = this.licensePlates().some(p => p.id === result.id);
          if (alreadyListed) {
            this.snackBar.open('That license plate is already on this parking right', 'Close', { duration: 3000 });
          } else {
            this.licensePlates.update(plates => [
              ...plates,
              { id: result.id!, fullNumber: result.fullNumber || value, parkerId }
            ]);
            this.snackBar.open('License plate added', 'Close', { duration: 2000 });
          }
          this.form.get('licensePlateInput')?.setValue('');
        }
        this.isAddingPlate.set(false);
      },
      error: (error) => {
        console.error('Error registering license plate:', error);
        this.snackBar.open('Failed to register license plate', 'Close', { duration: 3000 });
        this.isAddingPlate.set(false);
      }
    });
  }

  removeLicensePlate(plate: LicensePlateItem): void {
    this.licensePlates.update(plates => plates.filter(p => p.id !== plate.id));
  }

  onSubmit(): void {
    if (!this.parkingRight.id) return;

    if (this.isLongTerm) {
      this.submitLongTermEdit();
      return;
    }

    if (this.form.invalid || this.licensePlates().length === 0) {
      if (this.licensePlates().length === 0) {
        this.snackBar.open('Please add at least one license plate', 'Close', { duration: 3000 });
      }
      return;
    }

    this.isLoading.set(true);

    const formValue = this.form.value;
    let typeDto: CreateParkingRightRequestTypeDto;

    if (formValue.rightType === ParkingRightTypeEnum.TimeBased) {
      const timeBasedDto: TimeBasedParkingRightDto = {
        type: ParkingRightTypeEnum.TimeBased,
        validFrom: formValue.validFrom ? new Date(formValue.validFrom).toISOString() : null,
        validTo: formValue.validTo ? new Date(formValue.validTo).toISOString() : null,
        startTime: formValue.isAllDay ? null : formValue.startTime || null,
        endTime: formValue.isAllDay ? null : formValue.endTime || null,
        validDays: formValue.validDays?.length > 0 ? formValue.validDays : null,
        isAllDay: formValue.isAllDay,
        isRecurring: formValue.isRecurring
      };
      typeDto = timeBasedDto;
    } else {
      const durationBasedDto: DurationBasedParkingRightDto = {
        type: ParkingRightTypeEnum.DurationBased,
        validFrom: formValue.validFrom ? new Date(formValue.validFrom).toISOString() : null,
        validTo: formValue.validTo ? new Date(formValue.validTo).toISOString() : null,
        durationInMinutes: formValue.durationInMinutes || 60,
        isRenewable: formValue.isRenewable,
        maxRenewals: formValue.isRenewable ? formValue.maxRenewals : null
      };
      typeDto = durationBasedDto;
    }

    const request: EditParkingRightRequest = {
      typeDto,
      parkingTypeId: formValue.parkingTypeId,
      allowedParkingSpaces: [this.parkingSpace.id!],
      allowedLicensePlates: this.licensePlates().map(p => p.id),
      allowedParkingZones: formValue.selectedZones?.length > 0 ? formValue.selectedZones : null,
      comment: formValue.comment?.trim() || null
    };

    this.parkingRightService.parkingRightsParkingRightIdPut(this.parkingRight.id, request).subscribe({
      next: () => {
        this.snackBar.open('Parking right updated successfully', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error updating parking right:', error);
        this.snackBar.open('Failed to update parking right', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  private submitLongTermEdit(): void {
    if (this.licensePlates().length === 0) {
      this.snackBar.open('Please add a license plate', 'Close', { duration: 3000 });
      return;
    }
    this.isLoading.set(true);
    const formValue = this.form.value;
    const typeDto: TimeBasedParkingRightDto = {
      type: ParkingRightTypeEnum.TimeBased,
      validFrom: null,
      validTo: formValue.validToEnabled && formValue.validTo
        ? new Date(formValue.validTo).toISOString()
        : null,
      startTime: null,
      endTime: null,
      validDays: null,
      isAllDay: true,
      isRecurring: false
    };
    const request: EditParkingRightRequest = {
      typeDto,
      parkingTypeId: this.parkingRight.parkingTypeId,
      allowedParkingSpaces: [this.parkingSpace.id!],
      allowedLicensePlates: this.licensePlates().map(p => p.id),
      allowedParkingZones: formValue.selectedZones?.length > 0 ? formValue.selectedZones : null,
      name: formValue.name || null,
      comment: formValue.comment?.trim() || null
    };
    this.parkingRightService.parkingRightsParkingRightIdPut(this.parkingRight.id!, request).subscribe({
      next: () => {
        this.snackBar.open('Long-Term right updated', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.dialogRef.close(true);
      },
      error: () => {
        this.snackBar.open('Failed to update Long-Term right', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
